import type {
  AssertiveYieldManagerEnv,
  GtagEvent,
  Slot,
} from '../../types/assertive'
import debug from '../../utils/console'

const logger = debug.withTag('adops/slots')

// One GPT listener per event, so every part of the ad stack sees a slot's
// events in a known order and reads the same state for it.

export type SlotEvents = {
  slotRequested: { slot: Slot }
  slotRenderEnded: GtagEvent
  slotVisibilityChanged: { slot: Slot; inViewPercentage: number }
}

export type SlotEventName = keyof SlotEvents

/** What the ad stack knows about one defined slot, keyed by its instance code. */
export type SlotState = {
  /** GPT has sent a request for it. */
  requested: boolean
  /** Outcome of its last render; null until one ends. */
  filled: boolean | null
  inViewPercent: number
  /** AY refreshed it on the current page view. */
  refreshed: boolean
  /** The native fallback destroyed its instance. */
  fellBack: boolean
}

const UNKNOWN: SlotState = {
  requested: false,
  filled: null,
  inViewPercent: 0,
  refreshed: false,
  fellBack: false,
}

const states = new Map<string, SlotState>()
const owners = new Map<string, symbol>()
const subscribers: {
  [E in SlotEventName]: Set<(event: SlotEvents[E]) => void>
} = {
  slotRequested: new Set(),
  slotRenderEnded: new Set(),
  slotVisibilityChanged: new Set(),
}
let listening = false

export function slotState(code: string): SlotState {
  return states.get(code) ?? UNKNOWN
}

function update(code: string, change: Partial<SlotState>): void {
  states.set(code, { ...slotState(code), ...change })
}

function notify<E extends SlotEventName>(name: E, event: SlotEvents[E]): void {
  for (const fn of subscribers[name]) {
    // Each subscriber used to be its own GPT listener; one failing must not
    // stop the rest.
    try {
      fn(event)
    } catch (err) {
      logger.error(`${name} subscriber failed`, err)
    }
  }
}

/** Starts the GPT listeners once. Call before the first display(). */
export function listenToSlots(): void {
  if (listening) return
  listening = true
  window.googletag = window.googletag || { cmd: [] }
  window.googletag.cmd.push(() => {
    const pubads = window.googletag.pubads()
    pubads.addEventListener(
      'slotRequested',
      (event: SlotEvents['slotRequested']) => {
        update(event.slot.getSlotElementId(), { requested: true })
        notify('slotRequested', event)
      }
    )
    pubads.addEventListener('slotRenderEnded', (event: GtagEvent) => {
      update(event.slot.getSlotElementId(), { filled: !event.isEmpty })
      notify('slotRenderEnded', event)
    })
    pubads.addEventListener(
      'slotVisibilityChanged',
      (event: SlotEvents['slotVisibilityChanged']) => {
        update(event.slot.getSlotElementId(), {
          inViewPercent: event.inViewPercentage,
        })
        notify('slotVisibilityChanged', event)
      }
    )
  })
}

/** Subscribers run after the slot's state is updated, in subscription order. */
export function onSlotEvent<E extends SlotEventName>(
  name: E,
  fn: (event: SlotEvents[E]) => void
): () => void {
  listenToSlots()
  subscribers[name].add(fn)
  return () => {
    subscribers[name].delete(fn)
  }
}

export function markRefreshed(code: string): void {
  update(code, { refreshed: true })
}

// AY resets its refresh counts per page view.
export function clearRefreshed(): void {
  for (const [code, state] of states) {
    states.set(code, { ...state, refreshed: false })
  }
}

export function markFellBack(code: string): void {
  update(code, { fellBack: true })
}

// AY gives a new slot the code of one it destroyed, e.g. after a category tab
// change, so nothing known about the old slot may carry over.
export function forgetSlot(code: string): void {
  states.delete(code)
}

/**
 * Records which container displayed the slot defined under `code`. AY only
 * reuses the code of a destroyed slot, so a later claim means the earlier
 * owner's slot is gone and the code is no longer its to clean up.
 */
export function claimSlot(code: string, owner: symbol): void {
  owners.set(code, owner)
}

export function ownsSlot(code: string, owner: symbol): boolean {
  return owners.get(code) === owner
}

/** The owner is done with the slot: drop its state and its claim. */
export function releaseSlot(code: string): void {
  states.delete(code)
  owners.delete(code)
}

/** Keeps slot state in step with AY: reused codes, refreshes, page changes. */
export function followAy(env: Pick<AssertiveYieldManagerEnv, 'onEvent'>): void {
  env.onEvent('afterDefineSlot', (code: string) => forgetSlot(code))
  env.onEvent('afterRefresh', (codes: string[]) => codes.forEach(markRefreshed))
  env.onEvent('afterPageChange', clearRefreshed)
}

export function resetSlotsForTests(): void {
  states.clear()
  owners.clear()
  for (const set of Object.values(subscribers)) set.clear()
  listening = false
}
